import { matchIsValidTel } from 'mui-tel-input';
import { z } from 'zod';

import type { PaymentRequest } from '../types';

// Zod schema for payment request validation
export const paymentRequestSchema = z.object({
  amount: z
    .string()
    .min(1, 'Amount is required')
    .regex(/^\d+(\.\d{1,2})?$/, 'Amount must have at most 2 decimal places')
    .refine(val => parseFloat(val) > 0, 'Amount must be a positive number'),
  patientId: z.string().min(1, 'Patient is required'),
  reason: z.string().max(500, 'Reason must be less than 500 characters').optional(),
  dueDate: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val) return true; // Optional field
        const date = new Date(val);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return !isNaN(date.getTime()) && date >= today;
      },
      {
        message: 'Due date must be today or in the future',
      },
    ),
  phoneNumber: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val) return true; // Optional field
        return matchIsValidTel(val, {
          onlyCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX'],
        });
      },
      {
        message: 'Please enter a valid phone number',
      },
    ),
  appointmentId: z.string().optional(),
  encounterId: z.string().optional(),
  actionId: z.string().optional(),
  source: z.string().optional(),
  paymentMethodId: z.string().optional(),
});

// Type inference from schema
export type PaymentRequestFormData = z.infer<typeof paymentRequestSchema>;

// For backward compatibility with components
export type PaymentRequestFormErrors = z.inferFlattenedErrors<
  typeof paymentRequestSchema
>['fieldErrors'];

/**
 * Validates payment request form data using Zod
 * Returns errors object if validation fails, null if valid
 */
export function validatePaymentRequest(
  data: Partial<PaymentRequestFormData>,
): PaymentRequestFormErrors | null {
  const result = paymentRequestSchema.safeParse(data);

  if (!result.success) {
    const errors = result.error.flatten().fieldErrors;
    // Convert array errors to single string errors for compatibility
    const formattedErrors: Record<string, string | undefined> = {};
    Object.entries(errors).forEach(([key, value]) => {
      formattedErrors[key] = value[0];
    });
    return formattedErrors as PaymentRequestFormErrors;
  }

  return null;
}

/**
 * Formats amount to ensure it has 2 decimal places
 */
export function formatAmount(amount: string): string {
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return num.toFixed(2);
}

/**
 * Check if all required fields are present for a payment request
 */
export function hasRequiredFields(data: Partial<PaymentRequestFormData>): boolean {
  const requiredFieldsSchema = paymentRequestSchema.pick({
    amount: true,
    patientId: true,
  });

  return requiredFieldsSchema.safeParse(data).success;
}

/**
 * Convert form data to API request format
 */
export function formDataToRequest(data: PaymentRequestFormData): PaymentRequest {
  return {
    amount: formatAmount(data.amount),
    patient_id: data.patientId,
    reason: data.reason || '', // Default to empty string since API expects string
    due_date: data.dueDate || null,
    phone_number: data.phoneNumber || null,
    appointment_id: data.appointmentId || null,
    encounter_id: data.encounterId || null,
    source: data.source || null,
    payment_method_id: data.paymentMethodId || null,
  };
}
